import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FolderOpenIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { pobierzPodgladWykonawcy } from '../../api/teczka'
import { useTeczka } from '../../hooks/useTeczka'
import { podsumowanieProjektu } from '../../lib/teczka/dlaWykonawcy'
import { plural } from '../kits/format'
import TeczkaWykonawcy from './TeczkaWykonawcy'

/**
 * „Dołącz teczkę instalacji” w formularzu zlecenia na Rynku (plan „VoltValue dla domu”, P5, 2026-10-24, droga „Wiem,
 * czego chcę”). Pole formularza: `wartosc` to `teczka_id` albo null, `onZmiana(id | null)`. Pokazuje się tylko koncie
 * prywatnemu z teczką (backend z polem `teczka` w `/me`); komu nie, ten nie widzi niczego.
 *
 * „Co zobaczą wykonawcy” otwiera ten sam widok, który dostaną zaproszeni wykonawcy (`TeczkaWykonawcy`, dane
 * z `GET /api/v1/teczka/podglad-wykonawcy`), więc klient widzi, że adres, mapa, PPE i cena posiadanej oferty zostają u niego.
 */

// Eksport dla formularza zlecenia: przy źródle „Mój Projekt 3D” teczka idzie ze zleceniem bez pola wyboru (F9).
export const PodgladDlaWykonawcow = ({ onZamknij }) => {
  const tytulId = useId()
  const zamknijRef = useRef(null)
  const [stan, setStan] = useState({ ladowanie: true, teczka: null, blad: '' })

  useEffect(() => {
    let anulowane = false
    pobierzPodgladWykonawcy().then((w) => {
      if (anulowane) return
      setStan(w.ok ? { ladowanie: false, teczka: w.data.teczka, blad: '' }
        : { ladowanie: false, teczka: null, blad: w.httpStatus === 404 ? '' : w.error })
    })
    return () => { anulowane = true }
  }, [])

  useEffect(() => {
    zamknijRef.current?.focus()
    const esc = (e) => { if (e.key === 'Escape') onZamknij() }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [onZamknij])

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-gray-900/40 p-3 sm:items-center"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onZamknij() }}>
      <div role="dialog" aria-modal="true" aria-labelledby={tytulId}
           className="relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-800">
          <h2 id={tytulId} className="m-0 text-lg font-bold text-gray-900 dark:text-gray-100">Co zobaczą wykonawcy</h2>
          <button ref={zamknijRef} type="button" onClick={onZamknij} aria-label="Zamknij"
                  className="-mr-2 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4" aria-live="polite">
          {stan.ladowanie ? <p className="m-0 text-sm text-gray-500">Wczytuję…</p>
            : stan.blad ? <p role="alert" className="m-0 text-sm text-red-700 dark:text-red-400">{stan.blad}</p>
              : <TeczkaWykonawcy teczka={stan.teczka} wariant="klient" />}
        </div>
      </div>
    </div>
  )
}

const DolaczTeczke = ({ wartosc = null, onZmiana }) => {
  const { teczka, ustalanie, dostepna } = useTeczka()
  const [podglad, setPodglad] = useState(false)
  const zamknij = useCallback(() => setPodglad(false), [])
  const s = useMemo(() => podsumowanieProjektu(teczka?.projekt), [teczka])
  const nazwaId = useId()

  if (ustalanie || !dostepna) return null

  if (!s || !teczka?.id) {
    return (
      <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
        <p className="m-0 text-[15px] font-semibold text-gray-900 dark:text-gray-100">Teczka instalacji</p>
        <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
          Ułóż moduły na dachu w Projekcie 3D, a dołączymy do zlecenia rzut dachu, moc oraz licznik i rozdzielnicę.
        </p>
        <Link to="/projekt-3d" className="mt-2 inline-flex min-h-[40px] items-center text-sm font-semibold text-blue-700 hover:underline dark:text-blue-400">
          Otwórz Projekt 3D
        </Link>
      </div>
    )
  }

  const dolaczona = wartosc === teczka.id
  const zmien = (e) => onZmiana?.(e.target.checked ? teczka.id : null)

  return (
    <div className={`rounded-2xl border p-4 transition-colors ${dolaczona ? 'border-blue-600 bg-blue-50/50 dark:bg-blue-950/20' : 'border-gray-200 dark:border-gray-700'}`}>
      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" checked={dolaczona} onChange={zmien} aria-describedby={nazwaId}
               className="mt-0.5 h-5 w-5 flex-none cursor-pointer accent-blue-600" />
        <span className="min-w-0">
          <span className="flex items-center gap-2 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
            <FolderOpenIcon className="h-5 w-5 text-blue-600" aria-hidden="true" /> Dołącz teczkę instalacji
          </span>
          <span id={nazwaId} className="mt-1 block text-sm leading-6 text-gray-600 dark:text-gray-300">
            Rzut dachu, <span className="tabular-nums">{String(Math.round(s.mocKwp * 100) / 100).replace('.', ',')} kWp</span> (<span className="tabular-nums">{s.liczba}</span> {plural(s.liczba, 'moduł', 'moduły', 'modułów')}), licznik i rozdzielnica.
            {' '}<strong className="font-semibold text-blue-700">Wykonawcy zobaczą ją bez adresu i mapy.</strong>
          </span>
        </span>
      </label>
      <button type="button" onClick={() => setPodglad(true)}
              className="ml-8 mt-2 inline-flex min-h-[40px] items-center text-sm font-semibold text-blue-700 hover:underline dark:text-blue-400">
        Co zobaczą wykonawcy
      </button>
      {podglad && <PodgladDlaWykonawcow onZamknij={zamknij} />}
    </div>
  )
}

export default DolaczTeczke
